import {Injector}                    from '@angular/core';
import {TranslationStore}            from '../../stores/translation/translation.store';
import {ApDateService}               from '../../ap-core/services/ap-date-service';
import {GetRoundNumericService}      from '../../ap-utils/service/get-round-numeric.service';
import {SettingsStore}               from '../../stores/base-data/settings.store';
import {ExportFormatsStore}          from '../../stores/common/export-formats.store';
import {ExportTargetsStore}          from '../../stores/common/export-targets.store';
import {
  IExportFormatsComboData,
  IExportTargetsComboData
}                                    from '../components/wizard/wizard-ui-config/task-management-wizard-details.config';
import {BehaviorSubject, Observable} from 'rxjs';
import {TaskManagementConstants}     from '../task-management.constants';
import {APP_CONFIGURATION}           from '../../ap-core/config';
import {TerminalsStore}              from '../../stores/base-data/terminals.store';
import {UserContactsStore}           from '../../stores/base-data/user.contacts.store';
import {LoginStore}                  from '../../stores/login/login.store';
import {FertilizerStore}             from '../../stores/base-data/fertilizer.store';
import {ILegend}                     from '../../stores/map/map.legend.store';
import {TextProcessorService}        from '../../services/common/text-processor.service';
import {LEGEND_IDS}                  from '../../ap-interface/enums/ap-legend-ids.enum';
import {ApNutrientService}           from '../../ap-utils/service/ap-nutrient.service';
import {ApTooltipContentService}     from '../../ap-utils/service/ap-tooltip-content.service';
import {CropTypeStore}               from '../../stores/base-data/crop.types.store';
import {nameOf}                      from '../../ap-core/utils/ap-name-of';
import {IGridStatus}                 from '../../ap-interface/interfaces/ap-grid-status.interface';
import {ApColumnState}               from '../../ap-interface/enums/ap-column-state.enum';
import {MapLayerTooltipStore}        from '../../stores/map/map.layer.tooltip.store';
import IGuid = System.IGuid;
import IExportFormat = Data.Common.IExportFormat;
import ITaskManagementPlanSummary = Data.TaskManagement.ITaskManagementPlanSummary;
import ITaskManagementPlanBook = Data.TaskManagement.ITaskManagementPlanBook;

export abstract class TaskManagementDataProviderBase {
  private exportTargets$ = new BehaviorSubject<Array<IExportTargetsComboData>>(undefined);
  private selectedExportTarget$ = new BehaviorSubject<IExportTargetsComboData>(undefined);
  protected translationStore: TranslationStore;
  protected dateService: ApDateService;
  protected roundNumericService: GetRoundNumericService;
  protected settingsStore: SettingsStore;
  protected exportFormatsStore: ExportFormatsStore;
  protected exportTargetsStore: ExportTargetsStore;
  protected terminalsStore: TerminalsStore;
  protected userContactsStore: UserContactsStore;
  protected loginStore: LoginStore;
  protected fertilizerStore: FertilizerStore;
  protected textProcessorService: TextProcessorService;
  protected nutrientService: ApNutrientService;
  protected cropTypeStore: CropTypeStore;
  protected tooltipContentService: ApTooltipContentService;
  protected mapLayerTooltipStore: MapLayerTooltipStore;

  protected constructor(public baseInjector: Injector) {
    this.translationStore = baseInjector.get(TranslationStore);
    this.dateService = baseInjector.get(ApDateService);
    this.roundNumericService = baseInjector.get(GetRoundNumericService);
    this.settingsStore = baseInjector.get(SettingsStore);
    this.exportFormatsStore = baseInjector.get(ExportFormatsStore);
    this.exportTargetsStore = baseInjector.get(ExportTargetsStore);
    this.terminalsStore = baseInjector.get(TerminalsStore);
    this.userContactsStore = baseInjector.get(UserContactsStore);
    this.loginStore = baseInjector.get(LoginStore);
    this.fertilizerStore = baseInjector.get(FertilizerStore);
    this.textProcessorService = baseInjector.get(TextProcessorService);
    this.nutrientService = baseInjector.get(ApNutrientService);
    this.tooltipContentService = baseInjector.get(ApTooltipContentService);
    this.cropTypeStore = baseInjector.get(CropTypeStore);
    this.mapLayerTooltipStore = baseInjector.get(MapLayerTooltipStore);
  }

  public getStatus(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): IGridStatus {
    const state = summaryOrPlanBook.Status as number as ApColumnState;
    return {
      state,
      class: state === ApColumnState.Green
        ? 'ap-status-green'
        : state === ApColumnState.Yellow ? 'ap-status-yellow' : 'ap-status-red',
      description: ''
    };
  }

  public getApplicationModeText(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): string | undefined {
    if (!summaryOrPlanBook || !summaryOrPlanBook?.ApplicationMode?.Key) {
      return undefined;
    }
    return this.translationStore.FindTranslationForSelectedLanguage(summaryOrPlanBook.ApplicationMode.Key);
  }

  public getOperationModeText(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): string | undefined {
    if (!summaryOrPlanBook?.OperationMode?.Key) {
      return undefined;
    }
    return this.translationStore.FindTranslationForSelectedLanguage(summaryOrPlanBook.OperationMode.Key);
  }

  public getElementName(t: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    return t && t.Element ? this.translationStore.FindTranslationForSelectedLanguage(t.Element?.ShortKey) : '-';
  }

  public getAreaHaSum(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): number {
    if (!summaryOrPlanBook) {
      return 0;
    }
    return summaryOrPlanBook.AreaHa;
  }

  public getCropName(t: ITaskManagementPlanBook): string {
    return this.cropTypeStore.GetCropTypeName(t.MainCropId) ?? '';
  }

  public getProductTooltip(t: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    return this.tooltipContentService.getProductContentInfoTooltip(t.ProductId);
  }

  public getApplUnit(t: ITaskManagementPlanSummary | ITaskManagementPlanBook, perHa = false): string {
    let unit = 'Global__Unit_kg'; // fallback
    if (t?.ApplUnit && t.ApplUnit.BaseUnit) {
      unit = t.ApplUnit.BaseUnit.Key;
    } else if (t.ApplUnit) {
      unit = t.ApplUnit.Key;
    }
    // never '%/ha' => always only '%'
    if (unit === 'Global__Unit_Percent') {
      return this.translationStore.FindTranslationForSelectedLanguage(unit);
    }
    return this.translationStore.FindTranslationForSelectedLanguage(unit) +
      (perHa ? this.translationStore.FindTranslationForSelectedLanguage('Global__UnitPerHa') : '');
  }

  public getProductUnitBase(t: ITaskManagementPlanSummary | ITaskManagementPlanBook, perHa = false): string {
    const product = this.fertilizerStore.getFertilizer(t?.ProductId);
    let unit = 'Global__Unit_kg'; // fallback
    if (product && product.Unit && product.Unit.BaseUnit) {
      unit = product.Unit.BaseUnit.Key;
    } else if (product && product.Unit) {
      unit = product.Unit.Key;
    }
    // never '%/ha' => always only '%'
    if (unit === 'Global__Unit_Percent') {
      return this.translationStore.FindTranslationForSelectedLanguage(unit);
    }
    return this.translationStore.FindTranslationForSelectedLanguage(unit) +
      (perHa ? this.translationStore.FindTranslationForSelectedLanguage('Global__UnitPerHa') : '');
  }

  public getProductGridHeader(t: ITaskManagementPlanSummary): string {
    return this.translationStore.FindTranslationForSelectedLanguage('Global__Product') +
      ' [' + this.getProductUnitBase(t, true) + ']';
  }

  public getAlreadyExported(t: ITaskManagementPlanSummary): string {
    if (!t || !t.PlanBooks) {
      return '0/0';
    }
    return t.PlanBooks.filter(p => p.ExportedAt).length + '/' + t.PlanBooks.length;
  }

  public getFilteredExportFormats(exportFormatFilter: string[]): Array<IExportFormatsComboData> {
    return this.exportFormatsStore.Listen(s => s.data).getValue()
      .filter(f => exportFormatFilter.Contains(f.Key))
      .map((f) => {
        return {
          Id: f.Id,
          Key: f.Key,
          Description: f.Description,
          Translation: this.translationStore.FindTranslationForSelectedLanguage(f.Key)
        };
      });
  }

  public getExportTargets(selectedExportFormat?: IExportFormat): Observable<Array<IExportTargetsComboData>> {
    const exportTargets = new Array<IExportTargetsComboData>();

    // for now only Download
    exportTargets.AddRange(
      this.exportTargetsStore.Listen(s => s.data).getValue()
        .filter(f => f.Key === TaskManagementConstants.EXPORT_TARGET_DOWNLOAD)
        .map((f) => {
          return {
            Id: f.Id,
            Key: f.Key,
            Description: f.Description,
            Translation: this.translationStore.FindTranslationForSelectedLanguage(f.Key)
          };
        }));

    if (selectedExportFormat?.Key === TaskManagementConstants.EXPORT_FORMAT_SHP_POLYGON ||
      selectedExportFormat?.Key === TaskManagementConstants.EXPORT_FORMAT_SHP_MULTIPOLYGON ||
      selectedExportFormat?.Key === TaskManagementConstants.EXPORT_FORMAT_ISO ||
      selectedExportFormat?.Key === TaskManagementConstants.EXPORT_FORMAT_PFBOX_RST) {
      exportTargets.AddRange(this._getMailTargets());
    }

    if (selectedExportFormat?.Key === TaskManagementConstants.EXPORT_FORMAT_PDF) {
      exportTargets.AddRange(this._getMailTargets(false));
    }

    this.exportTargets$.next(exportTargets);
    return this.exportTargets$;
  }

  public getSelectedExportTarget(selectedExportTarget?: string): Observable<IExportTargetsComboData> {
    let selectedTarget = this.exportTargets$.getValue()?.find(t => t.Key === selectedExportTarget);
    if (!selectedTarget && this.exportTargets$.getValue()) {
      selectedTarget = this.exportTargets$.getValue()[0];
    }

    if (selectedTarget) {
      this.selectedExportTarget$.next(selectedTarget);
    } else {
      // in order to reset target it is necessary to set it to null and
      // then to undefined (to skip 'distinctUntilChanged' hook)
      this.selectedExportTarget$.next(null);
      this.selectedExportTarget$.next(undefined);
    }
    return this.selectedExportTarget$;
  }

  public getExportTargetText(exportTarget: string): string {
    if (!exportTarget) {
      return '';
    }
    // try to translate or display the value itself (most likely an email)
    return this.translationStore.FindTranslationForSelectedLanguage(exportTarget);
  }

  public getNumberRounded(n: number, _?: number): string {
    if (!n) {
      return '0';
    }
    return this.roundNumericService.round(n, 0);
    // https://jira.agricon.de/browse/APV49-1985
    // return this.roundNumericService.round(n, digits);
  }

  public getNumberRoundedNumber(n: number, digits?: number): number {
    if (!n) {
      return 0;
    }
    if (digits === undefined) {
      digits = this.settingsStore.FirstSetting ? this.settingsStore.FirstSetting.DigitsAfterDecimalPoint : 2;
    }
    return this.roundNumericService.roundAsNumber(n, digits);
  }

  public getDate(date: Date): Date {
    if (!date) {
      return null;
    }
    return this.dateService.toFarmDateFromUtcGetJsDate(date);
  }

  public getMapFactoryUrl(plans: ITaskManagementPlanBook[], provider: string, map: string): string {
    return `${APP_CONFIGURATION.MapFactory.Address}/map/${provider}/${map}/{z}/{x}/{y}?params=${JSON.stringify(plans.map(p => p.Id))}`;
  }

  public getPlanNumberTooltip(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    return `${this.translationStore.FindTranslationForSelectedLanguage('Global__Task')}
    ${this.translationStore.FindTranslationForSelectedLanguage('Docu_Ini__Number')}:
    ${summaryOrPlanBook?.PlanNumber}`;
  }

  public setSelectedGridIdFromPreviousPage(gridItemId: IGuid | undefined): void {
  }

  public selectLegendItem(t: ITaskManagementPlanSummary | ITaskManagementPlanBook): void {
    // per default => do nothing
  }

  protected isSummary(param: ITaskManagementPlanSummary | ITaskManagementPlanBook | ITaskManagementPlanBook[]): boolean {
    return Array.isArray(param[nameOf<ITaskManagementPlanSummary>('PlanBooks')]); // only summary has planbooks collection
  }

  protected convertToTaskMgmtLegend(legend: ILegend): ILegend {
    if (!legend) {
      return legend;
    }
    return {
      id: LEGEND_IDS.TASK_MANAGEMENT,
      title: legend.title,
      selectionLists: legend.selectionLists,
      subjects: legend.subjects,
      unit: legend.unit,
      values: legend.values,
      updateValues: legend.updateValues,
      getLastValueElement: legend.getLastValueElement,
      getFirstValueElement: legend.getFirstValueElement,
      getValueElementAt: legend.getValueElementAt,
      getValuesList: legend.getValuesList
    };
  }

  private _getMailTargets(addTerminals: boolean = true): Array<IExportTargetsComboData> {
    const mailTargets = new Array<IExportTargetsComboData>();

    let targetIdCount = 0;
    // add current user
    mailTargets.Add({
      Id: -1 * ++targetIdCount,
      Key: this.loginStore.User.EMail,
      Description: this.loginStore.User.EMail,
      Translation: this._buildHumanReadableExpr(this.loginStore.User.Surname,
        this.loginStore.User.Forename, this.loginStore.User.EMail)
    });

    // add user contacts:
    mailTargets.AddRange(this.userContactsStore.UserContacts
      .filter(_ => _.IsActive)
      .map(_ => {
        return {
          Id: -1 * ++targetIdCount,
          Key: _.MailAddress,
          Description: _.MailAddress,
          Translation: this._buildHumanReadableExpr(_.Lastname, _.Firstname, _.MailAddress)
        };
      }));

    if (addTerminals) {
      mailTargets.AddRange(this.terminalsStore.Terminals.map(_ => {
        return {
          Id: -1 * ++targetIdCount,
          Key: _.MailAddress,
          Description: _.MailAddress,
          Translation: this._buildHumanReadableExpr(_.Terminal, _.MailAddress)
        };
      }));
    }
    return mailTargets;
  }

  private _buildHumanReadableExpr(...values: string[]): string {
    if (!values || values.length === 0) {
      return '';
    }
    let resultText = '';
    values.forEach(v => {
      if (resultText?.length > 0 && v?.length > 0) {
        resultText = `${resultText}, `;
      }
      resultText = `${resultText}${v}`;
    });
    return resultText;
  }
}
